import React, { useState, useEffect, useCallback } from "react";
import { Modal, Form, Spinner } from "react-bootstrap";
import Select from "react-select";
import LoadingButton from "./LoadingButton";
import { formatMiles } from "../../../../utils/miles";

/**
 * Componente de Modal para crear / editar Asistencias.
 *
 * Cambios solicitados:
 *  - Manejo interno de spinner al Guardar / Actualizar.
 *  - Deshabilitar inputs y botones mientras se procesa.
 *  - Rehabilitar todo si la operación (onSave) retorna error (lanza excepción o devuelve {ok:false}).
 *
 * Contrato de onSave:
 *  - Debe ser una función async que lance error (throw) en caso de fallo.
 *  - Opcionalmente puede devolver un objeto { ok: true } al terminar.
 *  - Si el padre cierra el modal al terminar, no pasa nada; aquí igual marcamos success.
 */
const AsistenciaModal = ({
  show,
  onHide,
  isEditing,
  data,
  setData,
  procedimientos,
  procedimientoOptions,
  pacienteOptions,
  usuarioOptions,
  selectedProcedimiento,
  selectedPaciente,
  selectedUsuario,
  onSave,          // async => se encarga de persistir
  loading: externalLoadingProp, // si el padre decide controlar loading externamente (opcional)
  disabledPaciente = false,
  closeOnSuccess = true,        // si true intenta cerrar modal al éxito (si el padre no lo cierra solo)
}) => {
  // Si el padre no pasa loading, usamos interno.
  const [internalLoading, setInternalLoading] = useState(false);
  const loading = externalLoadingProp !== undefined ? externalLoadingProp : internalLoading;
  // Overlay de estado final (success / error) (opcional)
  const [status, setStatus] = useState(null); // 'success' | 'error' | null

  // Reset status cuando se abre/cierra
  useEffect(() => {
    if (!show) {
      setStatus(null);
      setInternalLoading(false);
    }
  }, [show]);

  const disabledAll = loading;

  const handleNumberChange = (e, field) => {
    if (disabledAll) return;
    let value = e.target.value.replace(/[^0-9]/g, "");
    if (value) {
      value = parseFloat(value).toLocaleString("es-ES");
    }
    setData({ ...data, [field]: value });
  };

  const handleProcedimientoChange = (selectedOption) => {
    if (disabledAll) return;
    const selectedProc = procedimientos.find(
      (proc) => proc.procedimiento === selectedOption?.value
    );
    setData({
      ...data,
      procedimiento: selectedOption ? selectedOption.value : "",
      tipo: selectedProc ? selectedProc.tipo : "",
      tarifa: selectedProc ? formatMiles(selectedProc.tarifa) : "",
    });
  };

  const handlePacienteChange = (selectedOption) => {
    if (disabledAll) return;
    setData({ ...data, paciente: selectedOption ? selectedOption.value : "" });
  };

  const handleUsuarioChange = (selectedOption) => {
    if (disabledAll) return;
    setData({ ...data, asignado: selectedOption ? selectedOption.value : "" });
  };

  const handleFileChange = (e) => {
    if (disabledAll) return;
    const file = e.target.files[0];
    setData({ ...data, foto: file });
  };

  // Opciones para los selects de paratarifa y parahonorario
  const siNoOptions = [
    { value: "SI", label: "SI" },
    { value: "NO", label: "NO" },
  ];

  const handleParaTarifaChange = (selectedOption) => {
    if (disabledAll) return;
    setData({ ...data, paratarifa: selectedOption ? selectedOption.value : "" });
  };

  const handleParaHonorarioChange = (selectedOption) => {
    if (disabledAll) return;
    setData({ ...data, parahonorario: selectedOption ? selectedOption.value : "" });
  };

  const selectedParaTarifa = siNoOptions.find(opt => opt.value === data.paratarifa);
  const selectedParaHonorario = siNoOptions.find(opt => opt.value === data.parahonorario);

  const executeSave = useCallback(async () => {
    if (externalLoadingProp === undefined) {
      setInternalLoading(true);
    }
    setStatus(null);
    try {
      const result = await onSave(); // el padre maneja la lógica (insert/update + refresh + cerrar modal)
      setStatus("success");
      if (closeOnSuccess) {
        // Si el padre no cierra el modal por sí mismo,
        // puedes descomentar la siguiente línea:
        // onHide();
      }
    } catch (err) {
      console.error("Error al guardar asistencia:", err);
      setStatus("error");
      // Rehabilitar UI si usamos loading interno
    } finally {
      if (externalLoadingProp === undefined) {
        setInternalLoading(false);
      }
    }
  }, [onSave, externalLoadingProp, closeOnSuccess]);

  return (
    <Modal show={show} onHide={loading ? undefined : onHide} backdrop="static" centered>
      <Modal.Header closeButton={!loading}>
        <Modal.Title>
          {isEditing ? "Editar Asistencia" : "Agregar Asistencia"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ position: "relative" }}>
        {/* Overlay de Loading */}
        {loading && (
          <div style={overlayStyles}>
            <Spinner animation="border" variant="primary" />
            <div className="mt-2" style={{ fontSize: 13, color: "#555" }}>
              {isEditing ? "Actualizando..." : "Guardando..."}
            </div>
          </div>
        )}

        <Form>
          <Form.Group className="mb-2">
            <Form.Label>Fecha</Form.Label>
            <Form.Control
              type="date"
              value={data.fecha}
              disabled={disabledAll}
              onChange={(e) => setData({ ...data, fecha: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Hora</Form.Label>
            <Form.Control
              type="time"
              value={data.hora}
              disabled={disabledAll}
              onChange={(e) => setData({ ...data, hora: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Paciente</Form.Label>
            <Select
              options={pacienteOptions}
              value={selectedPaciente}
              onChange={handlePacienteChange}
              isDisabled={disabledPaciente || disabledAll}
              placeholder="Seleccionar..."
              noOptionsMessage={() => "Sin datos"}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Procedimiento</Form.Label>
            <Select
              options={procedimientoOptions}
              value={selectedProcedimiento}
              onChange={handleProcedimientoChange}
              isDisabled={disabledAll}
              placeholder="Seleccionar..."
              noOptionsMessage={() => "Sin datos"}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Tipo</Form.Label>
            <Form.Control value={data.tipo} disabled />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Tarifa</Form.Label>
            <Form.Control
              value={data.tarifa}
              disabled={disabledAll}
              onChange={(e) => handleNumberChange(e, "tarifa")}
              placeholder="0"
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Honorario</Form.Label>
            <Form.Control
              value={data.honorario}
              disabled={disabledAll}
              onChange={(e) => handleNumberChange(e, "honorario")}
              placeholder="0"
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Asignado</Form.Label>
            <Select
              options={usuarioOptions}
              value={selectedUsuario}
              onChange={handleUsuarioChange}
              isDisabled={disabledAll}
              placeholder="Seleccionar..."
              noOptionsMessage={() => "Sin datos"}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Foto</Form.Label>
            <Form.Control
              type="file"
              disabled={disabledAll}
              onChange={handleFileChange}
              accept="image/*"
            />
          </Form.Group>
          {/* Opciones adicionales al final */}
          <Form.Group className="mt-3 mb-2">
            <Form.Label>Para Tarifa</Form.Label>
            <Select
              options={siNoOptions}
              value={selectedParaTarifa}
              onChange={handleParaTarifaChange}
              isDisabled={disabledAll}
              placeholder="Seleccionar..."
              noOptionsMessage={() => "Sin datos"}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Para Honorario</Form.Label>
            <Select
              options={siNoOptions}
              value={selectedParaHonorario}
              onChange={handleParaHonorarioChange}
              isDisabled={disabledAll}
              placeholder="Seleccionar..."
              noOptionsMessage={() => "Sin datos"}
            />
          </Form.Group>
        </Form>

        {/* Mensaje de error simple (opcional) */}
        {status === "error" && (
          <div className="mt-3 text-danger" style={{ fontSize: 13 }}>
            Ocurrió un error. Revisa los datos o inténtalo nuevamente.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <LoadingButton
          variant="danger"
          onClick={onHide}
          loading={false}
          disabled={loading}
        >
          Cancelar
        </LoadingButton>
        <LoadingButton
          variant="primary"
          onClick={executeSave}
          loading={loading}
          spinnerText={isEditing ? "Actualizando..." : "Guardando..."}
          disabled={loading}
        >
          {isEditing ? "Actualizar" : "Guardar"}
        </LoadingButton>
      </Modal.Footer>
    </Modal>
  );
};

const overlayStyles = {
  position: "absolute",
  inset: 0,
  background: "rgba(255,255,255,0.65)",
  backdropFilter: "blur(2px)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 10,
};

export default AsistenciaModal;